import el from "@cypherpotato/el";
import Temml from "temml";
import { displayAlert, displayFormControl } from "../ui/alert";
import { copyToClipboard } from "../ui/clipboard";
import { renderMarkdown } from "../ui/md-renderer";
import { ellipsis, randomId, readBlob } from "../util";
import { Button } from "./button";
import { Dropdown, DropdownItem } from "./dropdown";
import { TextArea } from "./form";
import { Icon } from "./icon";
import { Image } from "./image";
import { Warning } from "./warn";

const LargePasteLength = 1000;
const TextAreaMaxRows = 10;

function resource(culture, key, fallback) {
    const normalizedKey = key[0].toLowerCase() + key.slice(1);
    return culture?.[normalizedKey] ?? culture?.[key] ?? fallback;
}

function getTextContent(content) {
    if (typeof content === "string") {
        return content;
    }

    return (content || [])
        .filter(part => part?.type === "text")
        .map(part => part.text)
        .filter(Boolean)
        .join("\n\n");
}

function escapeBlock(text) {
    return el.escapeHtmlLiteral(text || "");
}

function getAudioSource(inputAudio) {
    if (!inputAudio?.data) {
        return "";
    }
    if (inputAudio.data.startsWith("data:")) {
        return inputAudio.data;
    }
    return `data:audio/${inputAudio.format || "wav"};base64,${inputAudio.data}`;
}

function getVideoSource(videoUrl) {
    return videoUrl?.url || "";
}

function getFileData(file) {
    return file?.file_data || file?.fileData || "";
}

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(event.target.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

function createAttachmentFromData({ type, name, contentBase64, mimeType, format }) {
    return {
        id: randomId(),
        type,
        name: name || `${type.toLowerCase()}-${randomId()}`,
        contentBase64,
        mimeType,
        format
    };
}

function detectAttachmentType(file) {
    if (file.type?.startsWith("image/")) {
        return "Image";
    }
    if (file.type?.startsWith("audio/")) {
        return "Audio";
    }
    if (file.type?.startsWith("video/")) {
        return "Video";
    }
    return "File";
}

function attachmentToContentPart(attachment) {
    if (attachment.type === "Text") {
        return {
            type: "text",
            text: attachment.contentBase64,
            inlineAttachment: true,
            filename: attachment.name
        };
    }
    if (attachment.type === "Image") {
        return { type: "image_url", image_url: { url: attachment.contentBase64 } };
    }
    if (attachment.type === "Audio") {
        return {
            type: "input_audio",
            input_audio: {
                data: attachment.contentBase64,
                format: attachment.format || attachment.mimeType?.split("/").at(-1) || "wav"
            }
        };
    }
    if (attachment.type === "Video") {
        return { type: "video_url", video_url: { url: attachment.contentBase64 } };
    }
    return {
        type: "file",
        file: {
            filename: attachment.name,
            file_data: attachment.contentBase64
        }
    };
}

function createAssistantRoot(text) {
    const parser = new DOMParser();
    const document = parser.parseFromString(`<div>${text || ""}</div>`, "text/html");
    return document.body.firstElementChild;
}

function meaningfulNodes(nodes) {
    return [...nodes].filter(node =>
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
    );
}

function lastAssistantAnswerIndex(nodes) {
    for (let i = nodes.length - 1; i >= 0; i--) {
        if (nodes[i].nodeType === Node.ELEMENT_NODE && nodes[i].tagName.toLowerCase() === "assistant-answer") {
            return i;
        }
    }
    return -1;
}

function renderMarkdownBlock(markdown, className = "") {
    return el(`.chat-v2-markdown.markdown-content${className}`,
        el.raw(renderMarkdown(markdown || ""))
    );
}

function isThinkingBlock(node) {
    const tag = node.tagName?.toLowerCase();
    return tag === "thinking-block" || tag === "thinking-group";
}

function isToolNode(node) {
    return node.nodeType === Node.ELEMENT_NODE && (
        node.tagName.toLowerCase() === "tool" ||
        node.classList.contains("tool-result")
    );
}

function isLastNodeTool(node) {
    if (!node) {
        return false;
    }
    if (isToolNode(node)) {
        return true;
    }
    if (node.nodeType === Node.ELEMENT_NODE && isThinkingBlock(node)) {
        return isLastNodeTool(meaningfulNodes(node.childNodes).at(-1));
    }
    return false;
}

function toolPartText(node, selector, fallback = "") {
    return node.querySelector?.(selector)?.textContent?.trim() || fallback;
}

function renderToolNode(node, previousWasTool, isActiveTool) {
    const name =
        node.dataset?.toolName ||
        toolPartText(node, "toolname") ||
        toolPartText(node, "b") ||
        "tool";

    const description =
        toolPartText(node, "toolreason") ||
        toolPartText(node, "span") ||
        node.textContent?.replace(name, "")?.trim() ||
        "";

    return el(".chat-v2-tool-call",
        {
            classList: [isActiveTool && "running", node.dataset?.toolState === "Failed" && "failed"],
            "data-tool-name": name
        },
        Icon("box-3-line", { classList: [previousWasTool && "hidden"] }),
        el("span.chat-v2-tool-name", name),
        description && el("span.chat-v2-tool-description", description)
    );
}

function flattenThinkingNodes(nodes) {
    return meaningfulNodes(nodes).flatMap(node => {
        if (isToolNode(node)) {
            return [{ type: "tool", node }];
        }
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === "think") {
            return flattenThinkingNodes(node.childNodes);
        }
        return [{ type: "think", markdown: node.textContent?.trim() || "" }];
    }).filter(item => item.type === "tool" || item.markdown);
}

function renderThinkingItems(items, isLastVisibleNode, isGenerating) {
    let previousWasTool = false;

    return el(".chat-v2-thinking-group",
        { "data-preserve-scroll": true },
        items.map((item, index) => {
            if (item.type === "tool") {
                const rendered = renderToolNode(
                    item.node,
                    previousWasTool,
                    isGenerating && isLastVisibleNode && index === items.length - 1
                );
                previousWasTool = true;
                return rendered;
            }

            previousWasTool = false;
            return el(".chat-v2-think",
                renderMarkdownBlock(item.markdown)
            );
        })
    );
}

function renderThinkingBlock(node, isLastVisibleNode, isGenerating) {
    return renderThinkingItems(flattenThinkingNodes(node.childNodes), isLastVisibleNode, isGenerating);
}

function renderAssistantNode(node, isLastVisibleNode, isGenerating, blockId) {
    if (node.nodeType === Node.TEXT_NODE) {
        return el(".chat-v2-answer-fragment", { "data-block-id": `${blockId}:text` }, renderMarkdownBlock(node.textContent));
    }

    const tag = node.tagName.toLowerCase();
    if (tag === "assistant-answer") {
        return el(".chat-v2-answer-fragment", { "data-block-id": `${blockId}:answer` }, renderMarkdownBlock(node.textContent));
    }
    if (isThinkingBlock(node)) {
        const thinkingBlock = renderThinkingBlock(node, isLastVisibleNode, isGenerating);
        thinkingBlock.dataset.blockId = `${blockId}:thinking`;
        return thinkingBlock;
    }
    if (tag === "think") {
        const thinkingBlock = renderThinkingItems(flattenThinkingNodes([node]), isLastVisibleNode, isGenerating);
        thinkingBlock.dataset.blockId = `${blockId}:think`;
        return thinkingBlock;
    }
    if (isToolNode(node)) {
        return el(".chat-v2-thinking-group",
            { "data-block-id": `${blockId}:tool` },
            renderToolNode(node, false, isGenerating && isLastVisibleNode)
        );
    }

    return el(".chat-v2-answer-fragment", { "data-block-id": `${blockId}:${tag}` }, renderMarkdownBlock(node.textContent));
}

function renderAssistantNodes(nodes, isGenerating = false, blockPrefix = "segment") {
    return nodes.map((node, index) =>
        renderAssistantNode(node, index === nodes.length - 1, isGenerating, `${blockPrefix}:${index}`)
    );
}

function renderAssistantFlow({ rawContent, isDone, error, reasoningLabel, thinkingLabel }) {
    const root = createAssistantRoot(rawContent);
    const segments = meaningfulNodes(root.childNodes);
    const answerIndex = isDone ? lastAssistantAnswerIndex(segments) : -1;
    const shouldCollapse = answerIndex > 0;
    const visibleSegments = shouldCollapse ? segments.slice(answerIndex) : segments;
    const hiddenSegments = shouldCollapse ? segments.slice(0, answerIndex) : [];
    const showThinking = !isDone && !error && !isLastSegmentTool(segments);

    return el(".chat-v2-assistant-flow",
        shouldCollapse && el("details.chat-v2-reasoning-history",
            el("summary", el("span", reasoningLabel), Icon("arrow-right-s-line")),
            el(".chat-v2-reasoning-history-content",
                { "data-preserve-scroll": true },
                renderAssistantNodes(hiddenSegments, false, "hidden")
            )
        ),
        renderAssistantNodes(visibleSegments, !isDone, "visible"),
        showThinking && el(".chat-v2-thinking-label.gradient-text", thinkingLabel)
    );
}

function morphKey(node) {
    return node.nodeType === Node.ELEMENT_NODE
        ? node.getAttribute("data-block-id") || node.id || null
        : null;
}

function sameMorphNode(current, next) {
    if (current.nodeType !== next.nodeType) {
        return false;
    }
    if (current.nodeType === Node.TEXT_NODE) {
        return true;
    }
    if (current.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }

    const currentKey = morphKey(current);
    const nextKey = morphKey(next);

    return current.tagName === next.tagName &&
        (!currentKey || !nextKey || currentKey === nextKey) &&
        current.getAttribute("data-tool-name") === next.getAttribute("data-tool-name") &&
        current.className === next.className;
}

function syncAttributes(current, next) {
    const preservedDetailsOpen = current.tagName === "DETAILS" && current.open;

    for (const attribute of [...current.attributes]) {
        if (!next.hasAttribute(attribute.name)) {
            current.removeAttribute(attribute.name);
        }
    }
    for (const attribute of [...next.attributes]) {
        if (current.getAttribute(attribute.name) !== attribute.value) {
            current.setAttribute(attribute.name, attribute.value);
        }
    }
    if (preservedDetailsOpen) {
        current.open = true;
    }
}

function syncNode(current, next) {
    if (!sameMorphNode(current, next)) {
        const replacement = next.cloneNode(true);
        current.replaceWith(replacement);
        return replacement;
    }

    if (current.nodeType === Node.TEXT_NODE) {
        if (current.nodeValue !== next.nodeValue) {
            current.nodeValue = next.nodeValue;
        }
        return current;
    }

    if (current.matches("[data-preserve]")) {
        return current;
    }

    const scrollTop = current.matches("[data-preserve-scroll]") ? current.scrollTop : null;
    const scrollLeft = current.matches("[data-preserve-scroll]") ? current.scrollLeft : null;
    const preserveChildren = current.matches("canvas, video, audio, iframe");

    syncAttributes(current, next);
    if (!preserveChildren) {
        syncChildren(current, next);
    }

    if (scrollTop !== null) {
        current.scrollTop = scrollTop;
        current.scrollLeft = scrollLeft;
    }

    return current;
}

function findUnusedChildByKey(children, key, usedChildren) {
    return key && children.find(child => !usedChildren.has(child) && morphKey(child) === key);
}

function syncChildren(current, next) {
    const currentChildren = [...current.childNodes];
    const nextChildren = [...next.childNodes];
    const usedChildren = new Set();

    for (let i = 0; i < nextChildren.length; i++) {
        const expectedChild = current.childNodes[i];
        const nextChild = nextChildren[i];
        const keyedChild = findUnusedChildByKey(currentChildren, morphKey(nextChild), usedChildren);
        const currentChild = keyedChild || (!usedChildren.has(expectedChild) && expectedChild);

        if (!currentChild) {
            const insertedChild = nextChild.cloneNode(true);
            current.insertBefore(insertedChild, current.childNodes[i] || null);
            usedChildren.add(insertedChild);
            continue;
        }

        if (currentChild !== expectedChild) {
            current.insertBefore(currentChild, expectedChild || null);
        }
        usedChildren.add(syncNode(currentChild, nextChild));
    }

    for (const currentChild of [...current.childNodes]) {
        if (!usedChildren.has(currentChild)) {
            currentChild.remove();
        }
    }
}

function getLastAnswerTextFromRoot(root) {
    const nodes = meaningfulNodes(root.childNodes);
    for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i];
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === "assistant-answer") {
            return node.textContent.trim();
        }
    }
    return "";
}

function getToolCallReason(toolCall) {
    try {
        const args = JSON.parse(toolCall.function?.arguments || "{}");
        return args._tool_reason || args._tool_goal || "";
    }
    catch {
        return "";
    }
}

function isLastSegmentTool(segments) {
    return isLastNodeTool(segments.at(-1));
}

const autoScrollReactivateDelta = 80;

function AssistantMessage({ initialContent, culture, options }) {
    const rawContent = el.state.create(initialContent || "");
    const isDone = el.state.create(Boolean(initialContent));
    const isThinking = el.state.create(false);
    const error = el.state.create(null);
    const playingState = el.state.create(null);

    const reasoningLabel = resource(culture, "ReasoningShowButton", "Show reasoning");
    const thinkingLabel = resource(culture, "StateThinking", "Thinking");
    let flowSlot;
    let assistantElement;
    let thinkingScrollElement;
    let canScrollThinkingToBottom = true;
    let lastThinkingScrollTop = 0;
    let thinkingWheelDeltaY = 0;
    let reasoningSegmentOpen = false;

    function closeReasoningSegment() {
        if (reasoningSegmentOpen) {
            rawContent.value += "\n</think>\n</thinking-group>\n\n";
            reasoningSegmentOpen = false;
        }
    }

    function scrollThinkingToBottom(force) {
        if (thinkingScrollElement && (canScrollThinkingToBottom || force)) {
            thinkingScrollElement.scrollTop = thinkingScrollElement.scrollHeight;
        }
    }

    function onThinkingWheel(event) {
        thinkingWheelDeltaY = event.deltaY;

        if (event.deltaY < 0) {
            canScrollThinkingToBottom = false;
        } else if (event.deltaY >= autoScrollReactivateDelta) {
            canScrollThinkingToBottom = true;
            requestAnimationFrame(() => scrollThinkingToBottom(false));
        }
    }

    function onThinkingScroll() {
        if (!thinkingScrollElement) {
            return;
        }

        const scrollTop = thinkingScrollElement.scrollTop;
        const scrollDelta = scrollTop - lastThinkingScrollTop;

        if (scrollDelta < 0) {
            canScrollThinkingToBottom = false;
        } else if (scrollDelta >= autoScrollReactivateDelta || thinkingWheelDeltaY >= autoScrollReactivateDelta) {
            canScrollThinkingToBottom = true;
        }

        lastThinkingScrollTop = scrollTop;
        thinkingWheelDeltaY = 0;
    }

    function watchThinkingScroll(element) {
        if (thinkingScrollElement === element) {
            return;
        }

        if (thinkingScrollElement) {
            thinkingScrollElement.removeEventListener("wheel", onThinkingWheel);
            thinkingScrollElement.removeEventListener("scroll", onThinkingScroll);
        }

        thinkingScrollElement = element;
        canScrollThinkingToBottom = true;
        lastThinkingScrollTop = element?.scrollTop || 0;
        thinkingWheelDeltaY = 0;

        if (thinkingScrollElement) {
            thinkingScrollElement.addEventListener("wheel", onThinkingWheel, { passive: true });
            thinkingScrollElement.addEventListener("scroll", onThinkingScroll, false);
        }
    }

    function updateThinkingScroll() {
        const groups = flowSlot ? [...flowSlot.querySelectorAll(".chat-v2-thinking-group")] : [];
        const activeThinkingGroup = !isDone.value && !error.value ? groups.at(-1) : null;

        watchThinkingScroll(activeThinkingGroup);
        scrollThinkingToBottom(false);
    }

    return el(".chat-v2-message.chat-v2-assistant-message",
        {
            init() {
                assistantElement = this;
            },
            $functions: {
                append(chunk) {
                    closeReasoningSegment();
                    rawContent.value += chunk;
                    isThinking.value = false;
                    this.updateAssistantFlow();
                },
                appendReasoning(chunk) {
                    if (!reasoningSegmentOpen) {
                        rawContent.value += "<thinking-group>\n<think>\n";
                        reasoningSegmentOpen = true;
                    }
                    rawContent.value += escapeBlock(chunk);
                    isThinking.value = false;
                    this.updateAssistantFlow();
                },
                setContent(chunk) {
                    rawContent.value = chunk;
                    reasoningSegmentOpen = false;
                    isThinking.value = false;
                    this.updateAssistantFlow();
                },
                startImplicitThinking() {
                    isThinking.value = true;
                    this.updateAssistantFlow();
                },
                setDone() {
                    closeReasoningSegment();
                    isThinking.value = false;
                    isDone.value = true;
                    this.updateAssistantFlow();
                    requestAnimationFrame(() => this.renderEnhancements());
                },
                setError(message) {
                    isThinking.value = false;
                    error.value = message;
                    this.updateAssistantFlow();
                },
                getAssistantAnswerText() {
                    return getLastAnswerTextFromRoot(createAssistantRoot(rawContent.value));
                },
                setPlayingState(state) {
                    playingState.value = state;
                },
                updateAssistantFlow() {
                    if (!flowSlot?.firstElementChild) {
                        return;
                    }

                    syncNode(flowSlot.firstElementChild, renderAssistantFlow({
                        rawContent: rawContent.value,
                        isDone: isDone.value,
                        error: error.value,
                        reasoningLabel,
                        thinkingLabel
                    }));

                    updateThinkingScroll();
                },
                renderEnhancements() {
                    Prism.highlightAll();
                    window.temml = Temml;

                    for (const math of this.querySelectorAll("span.math")) {
                        math.innerHTML = Temml.renderToString(math.textContent);
                    }

                    for (const anchor of this.querySelectorAll("a")) {
                        anchor.setAttribute("target", "_blank");
                        anchor.setAttribute("rel", "noopener noreferrer");
                    }
                }
            }
        },
        el(".chat-v2-assistant-flow-slot",
            {
                init() {
                    flowSlot = this;
                    this.appendChild(renderAssistantFlow({
                        rawContent: rawContent.value,
                        isDone: isDone.value,
                        error: error.value,
                        reasoningLabel,
                        thinkingLabel
                    }));
                },
                mount() {
                    assistantElement?.updateAssistantFlow();
                    updateThinkingScroll();
                }
            }
        ),
        el.state.listening([error], () =>
            el(".chat-v2-inline-error-slot",
                error.value && el(".chat-v2-inline-error", Warning(error.value))
            )
        ),
        el.state.listening([rawContent, isDone, playingState], () =>
            el(".chat-v2-message-actions-slot",
                isDone.value && el(".chat-v2-message-actions",
                    Button(Icon("file-copy-line"), {
                        title: resource(culture, "CopyButtonLabel", "Copy"),
                        onClick() {
                            copyToClipboard(getLastAnswerTextFromRoot(createAssistantRoot(rawContent.value)));
                        }
                    }),
                    options?.onSinthetize && el.fragment(
                        Button(Icon(playingState.value === "loading" ? "loader-4-line" : "volume-up-line"), {
                            classList: [playingState.value === "playing" && "hidden"],
                            title: "Listen",
                            onClick() {
                                options.onSinthetize({
                                    sender: this.closest(".chat-v2-assistant-message"),
                                    state: "play",
                                    text: getLastAnswerTextFromRoot(createAssistantRoot(rawContent.value))
                                });
                            }
                        }),
                        Button(Icon("stop-fill"), {
                            classList: [playingState.value !== "playing" && "hidden"],
                            title: "Stop",
                            onClick() {
                                options.onSinthetize({
                                    sender: this.closest(".chat-v2-assistant-message"),
                                    state: "stop",
                                    text: ""
                                });
                            }
                        })
                    ),
                    options?.onRetry && Button(Icon("reset-right-line"), {
                        title: resource(culture, "TryAgain", "Try again"),
                        onClick() {
                            options.onRetry(this.closest(".chat-v2-message"));
                        }
                    })
                )
            )
        )
    );
}

function renderUserAttachment(part, isInlineText) {
    if (isInlineText) {
        return el(".chat-v2-user-inline-attachment",
            Icon("code-line"),
            el("pre", part.text)
        );
    }
    if (part.type === "image_url") {
        const src = part.image_url?.url;
        return el("button.chat-v2-user-image-attachment",
            Image(src),
            {
                onClick() {
                    displayAlert({
                        title: "Image",
                        dialogSize: "lightbox",
                        message: [Image(src)]
                    });
                }
            }
        );
    }
    if (part.type === "input_audio") {
        return el(".chat-v2-user-audio-attachment",
            Icon("volume-up-line"),
            el("audio[controls]", { src: getAudioSource(part.input_audio) })
        );
    }
    if (part.type === "video_url") {
        return el(".chat-v2-user-file-attachment",
            Icon("video-on-line"),
            el("span", ellipsis(getVideoSource(part.video_url), 44))
        );
    }
    if (part.type === "file") {
        return el(".chat-v2-user-file-attachment",
            Icon("attachment-line"),
            el("span", part.file?.filename || ellipsis(getFileData(part.file), 44) || "File")
        );
    }
}

function UserMessage({ content }) {
    const parts = typeof content === "string"
        ? [{ type: "text", text: content }]
        : content || [];

    const textParts = parts.filter(part => part?.type === "text" && part.text?.trim());
    const primaryText = textParts.find(part => !part.inlineAttachment && part.text.length <= LargePasteLength)?.text;
    const attachmentNodes = [];

    for (const part of parts) {
        if (!part?.type) {
            continue;
        }
        if (part.type === "text") {
            if (part.text !== primaryText) {
                attachmentNodes.push(renderUserAttachment(part, true));
            }
        }
        else {
            attachmentNodes.push(renderUserAttachment(part, false));
        }
    }

    return el(".chat-v2-message.chat-v2-user-message",
        primaryText && el(".chat-v2-user-text", primaryText),
        attachmentNodes.length > 0 && el(".chat-v2-user-attachments", attachmentNodes)
    );
}

function messageContentToAssistantRaw(message) {
    const text = getTextContent(message.content);
    return /<\s*(assistant-answer|thinking-group)\b/i.test(text)
        ? text
        : [
            (message.reasoning_content || message.tool_calls?.length > 0) && `<thinking-group>
${message.reasoning_content ? `<think>\n${escapeBlock(message.reasoning_content)}\n</think>` : ""}
${(message.tool_calls || []).map(toolCall => `<tool><toolname>${escapeBlock(toolCall.function?.name || "tool")}</toolname><toolreason>${escapeBlock(getToolCallReason(toolCall))}</toolreason></tool>`).join("\n")}
</thinking-group>`,
            text && `<assistant-answer>\n${escapeBlock(text)}\n</assistant-answer>`
        ].filter(Boolean).join("\n\n");
}

function messageContentToReasoningRaw(message) {
    const text = getTextContent(message.content);
    const hasToolMarkup = /<\s*(tool|div)\b/i.test(text) && (text.includes("tool-result") || text.includes("<tool"));

    return hasToolMarkup
        ? `<thinking-group>\n${text}\n</thinking-group>`
        : `<thinking-group>\n<think>\n${escapeBlock(text)}\n</think>\n</thinking-group>`;
}

function buildMessageElement(message, culture, options) {
    if (message.role === "user") {
        return UserMessage({ content: message.content });
    }

    const assistant = AssistantMessage({
        initialContent: message.content,
        culture,
        options
    });
    assistant.setDone();
    return assistant;
}

function normalizeMessageList(messageList) {
    return (messageList || [])
        .filter(message => message?.role && (message.content || message.reasoning_content || message.tool_calls?.length > 0))
        .reduce((messages, message) => {
            const lastMessage = messages.at(-1);
            const isReasoningMessage = message.role === "reasoning" || message.role === "hidden" || message.role === "verbose";

            if (message.role === "assistant") {
                const rawContent = messageContentToAssistantRaw(lastMessage?.isReasoningOnly
                    ? { ...message, reasoning_content: null }
                    : message);
                if (lastMessage?.role === "assistant") {
                    lastMessage.content = [lastMessage.content, rawContent].filter(Boolean).join("\n\n");
                    lastMessage.isReasoningOnly = false;
                }
                else {
                    messages.push({ role: "assistant", content: rawContent, isReasoningOnly: false });
                }
            }
            else if (isReasoningMessage) {
                const rawContent = messageContentToReasoningRaw(message);
                if (lastMessage?.role === "assistant") {
                    lastMessage.content = [lastMessage.content, rawContent].filter(Boolean).join("\n\n");
                }
                else {
                    messages.push({ role: "assistant", content: rawContent, isReasoningOnly: true });
                }
            }
            else {
                messages.push(message);
            }

            return messages;
        }, []);
}

function PendingAttachment(attachment, onRemove) {
    const preview = attachment.type === "Image"
        ? Image(attachment.contentBase64)
        : Icon(attachment.type === "Audio" ? "volume-up-line" : attachment.type === "Video" ? "video-on-line" : attachment.type === "Text" ? "code-line" : "attachment-line");

    return el(".chat-v2-pending-attachment",
        el(".chat-v2-pending-preview", preview),
        el("span", ellipsis(attachment.name, 32)),
        Button(Icon("close-line"), {
            title: "Remove",
            onClick() {
                onRemove(attachment);
            }
        })
    );
}

function ChatInput({
    attachments,
    clientOptions,
    culture,
    disabled,
    isInferencing,
    retryButton,
    onMessage,
    onCancel
}) {
    const textValue = el.state.create("");
    const recordingState = el.state.create("idle");
    let textArea;
    let mediaRecorder;
    let recordingStream;
    let recordingChunks = [];

    function updateTextAreaHeight() {
        if (!textArea) {
            return;
        }

        const lineHeight = Number.parseFloat(getComputedStyle(textArea).lineHeight) || 22;
        const maxHeight = lineHeight * TextAreaMaxRows + 24;
        textArea.style.height = "auto";
        textArea.style.height = `${Math.min(textArea.scrollHeight, maxHeight)}px`;
        textArea.style.overflowY = textArea.scrollHeight > maxHeight ? "auto" : "hidden";
    }

    async function includeFile(file) {
        if (!file) {
            return;
        }

        attachments.value = [
            ...attachments.value,
            createAttachmentFromData({
                type: detectAttachmentType(file),
                name: file.name,
                contentBase64: await readFileAsDataUrl(file),
                mimeType: file.type,
                format: file.type?.startsWith("audio/") ? file.type.split("/").at(-1) : null
            })
        ];
    }

    function includeInlineText(text, name = null) {
        attachments.value = [
            ...attachments.value,
            createAttachmentFromData({
                type: "Text",
                name: name || `inline-content-${randomId()}.txt`,
                contentBase64: text,
                mimeType: "text/plain"
            })
        ];
    }

    async function stopRecording() {
        if (!mediaRecorder || recordingState.value === "idle") {
            return;
        }

        const stopped = new Promise(resolve => {
            mediaRecorder.onstop = () => resolve(new Blob(recordingChunks, { type: mediaRecorder.mimeType || "audio/webm" }));
        });

        mediaRecorder.stop();
        recordingStream?.getTracks().forEach(track => track.stop());
        const audioBlob = await stopped;
        const contentBase64 = await readBlob(audioBlob);
        attachments.value = [
            ...attachments.value,
            createAttachmentFromData({
                type: "Audio",
                name: `recording-${randomId()}.webm`,
                contentBase64,
                mimeType: audioBlob.type,
                format: audioBlob.type.split("/").at(-1)?.split(";").at(0) || "webm"
            })
        ];
        recordingState.value = "idle";
        recordingChunks = [];
    }

    async function startRecording() {
        try {
            recordingStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorder = new MediaRecorder(recordingStream);
            recordingChunks = [];
            mediaRecorder.ondataavailable = event => {
                if (event.data.size > 0) {
                    recordingChunks.push(event.data);
                }
            };
            mediaRecorder.start();
            recordingState.value = "recording";
        }
        catch (error) {
            displayAlert({ message: error.message });
        }
    }

    function submit() {
        const text = textArea.value.trim();
        if (disabled.value || isInferencing.value || (!text && attachments.value.length === 0)) {
            return;
        }

        const sentAttachments = [ ...attachments.value ];
        attachments.value = [];
        textArea.value = "";
        textValue.value = "";
        updateTextAreaHeight();
        onMessage(text, sentAttachments);
    }

    function openInlineEditor() {
        displayFormControl({
            title: resource(culture, "AttachmentInlineDocumentTitle", "Add inline content"),
            formControlItem: TextArea(null, null, { rows: 12 }),
            dialogSize: "large",
            onConfirm(value) {
                if (value?.trim()) {
                    includeInlineText(value.trim());
                }
            }
        });
    }

    function openFilePicker() {
        const fileInput = el("input[type=file]", {
            multiple: true,
            onChange(event) {
                [...event.target.files].forEach(file => includeFile(file));
            }
        });
        fileInput.click();
    }

    return el(".chat-v2-input-container",
        retryButton,
        el.state.listening([attachments], () =>
            el(".chat-v2-pending-attachments-slot",
                attachments.value.length > 0 && el(".chat-v2-pending-attachments",
                    attachments.value.map(attachment =>
                        PendingAttachment(attachment, item => {
                            attachments.value = attachments.value.filter(current => current !== item);
                        })
                    )
                )
            )
        ),
        el.state.listening([recordingState], () =>
            el(".chat-v2-recording-slot",
                recordingState.value !== "idle" && el(".chat-v2-recording-bar",
                    el(".chat-v2-recording-state", Icon("record-circle-fill"), recordingState.value === "paused" ? "Paused" : "Recording"),
                    Button(recordingState.value === "paused" ? Icon("play-line") : Icon("pause-line"), {
                        onClick() {
                            if (recordingState.value === "paused") {
                                mediaRecorder.resume();
                                recordingState.value = "recording";
                            }
                            else {
                                mediaRecorder.pause();
                                recordingState.value = "paused";
                            }
                        }
                    }),
                    Button(Icon("stop-fill"), {
                        onClick() {
                            stopRecording();
                        }
                    })
                )
            )
        ),
        el(".chat-v2-input-shell",
            el("textarea.chat-v2-input",
                {
                    placeholder: clientOptions?.textAreaPlaceholder?.nullWhenEmpty?.() || resource(culture, "InputPlaceholder", "Talk to the assistant"),
                    rows: 1,
                    autocomplete: "false",
                    autocorrect: "false",
                    autocapitalize: "false",
                    spellcheck: "false",
                    onInput(event) {
                        textValue.value = event.target.value;
                        updateTextAreaHeight();
                    },
                    onKeyDown(event) {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            submit();
                        }
                    },
                    onPaste(event) {
                        const text = event.clipboardData?.getData("text/plain");
                        if (text && text.length > LargePasteLength) {
                            event.preventDefault();
                            includeInlineText(text);
                            return;
                        }

                        for (const item of event.clipboardData?.items || []) {
                            if (item.kind === "file") {
                                event.preventDefault();
                                includeFile(item.getAsFile());
                            }
                        }
                    },
                    mount() {
                        textArea = this;
                        updateTextAreaHeight();
                    }
                }
            ),
            el(".chat-v2-input-actions",
                Dropdown(
                    Icon("attachment-line"),
                    DropdownItem(resource(culture, "AttachmentDocumentTitle", "Attach file"), Icon("attachment-line"), {
                        onClick() {
                            openFilePicker();
                        }
                    }),
                    DropdownItem(resource(culture, "AttachmentInlineDocumentTitle", "Add inline content"), Icon("code-line"), {
                        onClick() {
                            openInlineEditor();
                        }
                    })
                ),
                el.state.listening([textValue, attachments, isInferencing, disabled], () => {
                    if (isInferencing.value) {
                        return Button(Icon("close-line"), resource(culture, "CancelButtonLabel", "Cancel"), {
                            classList: ["chat-v2-cancel-button"],
                            onClick() {
                                onCancel();
                            }
                        });
                    }
                    if (!textValue.value.trim() && attachments.value.length === 0 && clientOptions?.inputModes?.includes?.("Audio")) {
                        return Button(Icon("mic-line"), {
                            classList: ["chat-v2-record-button"],
                            disabled: disabled.value,
                            onClick() {
                                startRecording();
                            }
                        });
                    }

                    return Button(Icon("arrow-up-line"), {
                        classList: ["chat-v2-send-button"],
                        disabled: disabled.value || (!textValue.value.trim() && attachments.value.length === 0),
                        onClick() {
                            submit();
                        }
                    });
                })
            )
        )
    );
}

export function ChatV2({
    messageList,
    readOnly = false,
    onMessage,
    onClear,
    onSinthetize,
    onRetry,
    onCancel,
    clientOptions,
    culture,
    presentation,
    header,
    props
}) {
    const attachments = el.state.create([]);
    const disabled = el.state.create(readOnly);
    const isInferencing = el.state.create(false);
    const messages = normalizeMessageList(messageList);
    const hasMessages = el.state.create(messages.length > 0);
    const showSuggestions = el.state.create(messages.length === 0);
    const showRetryButton = el.state.create(messages.at(-1)?.role === "user");
    let canScrollToBottom = true;
    let lastScrollTop = 0;
    let pageWheelDeltaY = 0;

    function sendMessage(message, attachments) {
        hasMessages.value = true;
        showSuggestions.value = false;
        showRetryButton.value = false;
        onMessage(message, attachments);
    }

    function runRetry() {
        if (isInferencing.value || !onRetry) {
            return;
        }

        showRetryButton.value = false;
        isInferencing.value = true;
        onRetry();
    }

    if (!readOnly) {
        document.addEventListener("wheel", event => {
            pageWheelDeltaY = event.deltaY;

            if (event.deltaY < 0) {
                canScrollToBottom = false;
            } else if (event.deltaY >= autoScrollReactivateDelta) {
                canScrollToBottom = true;
            }
        }, { passive: true });

        document.addEventListener("scroll", () => {
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            const scrollDelta = scrollTop - lastScrollTop;

            if (scrollDelta < 0) {
                canScrollToBottom = false;
            } else if (scrollDelta >= autoScrollReactivateDelta || pageWheelDeltaY >= autoScrollReactivateDelta) {
                canScrollToBottom = true;
            }

            lastScrollTop = scrollTop;
            pageWheelDeltaY = 0;
        }, false);
    }

    return el(".chat-v2",
        header && el("header.chat-v2-header",
            el(".chat-v2-header-spacer"),
            el(".chat-v2-title", header.title),
            el(".chat-v2-header-actions",
                onClear && Button(Icon("reset-right-line"), {
                    classList: ["chat-v2-header-button"],
                    title: resource(culture, "ClearChatText", "Clear chat"),
                    onClick() {
                        onClear.call();
                    }
                })
            )
        ),
        el.state.listening([hasMessages, showSuggestions], () =>
            el(".chat-v2-empty-state",
                presentation && !hasMessages.value && el(".chat-v2-presentation",
                    presentation.logoSrc && el(".chat-v2-presentation-logo", Image(presentation.logoSrc)),
                    el(".chat-v2-presentation-title", presentation.helloTitle || resource(culture, "HelloLabel", "Hello.")),
                    el(".chat-v2-presentation-subtitle", presentation.helloSubtitle || resource(culture, "HelloSubLabel", "How can I help you?"))
                ),
                !readOnly && clientOptions?.suggestionButtons?.length > 0 && showSuggestions.value && el(".chat-v2-suggestions",
                    clientOptions.suggestionButtons.map(button =>
                        Button(button.label, {
                            onClick() {
                                sendMessage(button.prompt, []);
                            }
                        })
                    )
                )
            )
        ),
        el(".chat-v2-messages",
            messages.map(message => buildMessageElement(message, culture, { onSinthetize, onRetry }))
        ),
        !readOnly && ChatInput({
            attachments,
            clientOptions,
            culture,
            disabled,
            isInferencing,
            retryButton: el.state.listening([showRetryButton, isInferencing], () =>
                el(".chat-v2-retry-slot",
                    showRetryButton.value && !isInferencing.value && onRetry && Button(
                        Icon("reset-right-line"),
                        resource(culture, "TryAgain", "Retry"),
                        {
                            classList: ["chat-v2-retry-button"],
                            onClick() {
                                runRetry();
                            }
                        }
                    )
                )
            ),
            onMessage: sendMessage,
            onCancel
        }),
        {
            $properties: {
                isReadOnly: {
                    get() {
                        return disabled.value;
                    },
                    set(value) {
                        disabled.value = value;
                    }
                },
                isInferencing: {
                    get() {
                        return isInferencing.value;
                    },
                    set(value) {
                        isInferencing.value = value;
                    }
                }
            },
            $functions: {
                addFile(file) {
                    readFileAsDataUrl(file).then(contentBase64 => {
                        attachments.value = [
                            ...attachments.value,
                            createAttachmentFromData({
                                type: detectAttachmentType(file),
                                name: file.name,
                                contentBase64,
                                mimeType: file.type,
                                format: file.type?.startsWith("audio/") ? file.type.split("/").at(-1) : null
                            })
                        ];
                    });
                },
                createMessage(role, content) {
                    hasMessages.value = true;
                    showSuggestions.value = false;
                    showRetryButton.value = role === "user";

                    const message = role === "user"
                        ? UserMessage({ content })
                        : AssistantMessage({ initialContent: content || "", culture, options: { onSinthetize, onRetry } });

                    this.querySelector(".chat-v2-messages").appendChild(message);
                    return message;
                },
                renderEnhancements() {
                    this.querySelectorAll(".chat-v2-assistant-message").forEach(message => message.renderEnhancements());
                },
                scrollToBottom(force) {
                    if (canScrollToBottom || force) {
                        window.scrollTo({ top: document.body.scrollHeight, behavior: force ? "instant" : "smooth" });
                    }
                }
            },
            mount() {
                this.renderEnhancements();
                if (!readOnly) {
                    setTimeout(() => this.scrollToBottom(true), 250);
                }
            }
        },
        props
    );
}

export function attachmentsToContentParts(message, attachments) {
    return [
        message && { type: "text", text: message },
        ...attachments.map(attachmentToContentPart)
    ].filter(Boolean);
}
